/**
 * Инструкция «Как подключить MCP» — содержимое карточки в профиле,
 * рядом с созданием токенов (одно место вместо отдельной страницы).
 *
 * Адрес сервера берётся из текущего origin, чтобы инструкция подходила
 * и для прода, и для локального стенда.
 */
import { Alert, Table, Tabs, Typography } from 'antd';
import styles from './McpGuide.module.css';

const { Text } = Typography;

const TOKEN_PLACEHOLDER = 'lens_pat_ВАШ_ТОКЕН';

function CodeBlock({ code, caption }: { code: string; caption?: string }): JSX.Element {
  return (
    <div className={styles.code}>
      {caption ? <div className={styles.codeCaption}>{caption}</div> : null}
      <pre className={styles.pre}>{code}</pre>
      <div className={styles.copy}>
        <Typography.Text copyable={{ text: code, tooltips: ['Скопировать', 'Скопировано'] }} />
      </div>
    </div>
  );
}

const TROUBLES = [
  {
    key: 'not-listed',
    symptom: 'Сервера нет в списке инструментов',
    fix: 'Клиент не перезапущен. Закройте его полностью (не только окно) и откройте заново.',
  },
  {
    key: '401',
    symptom: '401 / «проверьте токен»',
    fix: 'Опечатка в токене, потеряно слово Bearer перед ним или токен отозван — создайте новый.',
  },
  {
    key: '403',
    symptom: '403',
    fix: 'Не хватает роли в пространстве. Попросите его администратора добавить вас участником.',
  },
  {
    key: '404',
    symptom: '404',
    fix: 'Неверные идентификаторы — попросите ассистента сначала вызвать lens_list_spaces.',
  },
];

export function McpGuide(): JSX.Element {
  const origin = typeof window === 'undefined' ? '' : window.location.origin;
  const serverUrl = `${origin}/mcp`;

  const jsonConfig = `{
  "mcpServers": {
    "lens": {
      "type": "http",
      "url": "${serverUrl}",
      "headers": {
        "Authorization": "Bearer ${TOKEN_PLACEHOLDER}"
      }
    }
  }
}`;

  const cursorConfig = `{
  "mcpServers": {
    "lens": {
      "url": "${serverUrl}",
      "headers": {
        "Authorization": "Bearer ${TOKEN_PLACEHOLDER}"
      }
    }
  }
}`;

  const codexConfig = `[mcp_servers.lens]
url = "${serverUrl}"
http_headers = { Authorization = "Bearer ${TOKEN_PLACEHOLDER}" }`;

  const claudeCodeCmd = `claude mcp add --transport http lens ${serverUrl} \\
  --header "Authorization: Bearer ${TOKEN_PLACEHOLDER}"`;

  return (
    <div className={styles.guide} data-testid="mcp-guide">
      <p className={styles.lead}>
        Lens умеет работать как MCP-сервер: AI-ассистент получает доступ к вашим пространствам,
        записям, справочникам и комментариям — ровно с теми же правами, что и у вас в вебе. Можно
        попросить «покажи все цели на Q3», «заведи записи по этому списку», «добавь комментарий».
      </p>
      <p className={styles.lead}>
        Понадобится токен из блока выше (секрет вида <Text code>lens_pat_…</Text> показывается один
        раз). Адрес сервера — <Text code copyable>{serverUrl}</Text>, транспорт HTTP (streamable),
        авторизация заголовком <Text code>Authorization: Bearer …</Text>. Ниже — готовые блоки,
        подставьте в них свой токен.
      </p>

      <Tabs
        className={styles.tabs}
        items={[
          {
            key: 'claude-code',
            label: 'Claude Code',
            children: (
              <>
                <CodeBlock caption="Одной командой в терминале" code={claudeCodeCmd} />
                <CodeBlock caption="Или вручную — .mcp.json в корне проекта" code={jsonConfig} />
                <p className={styles.hint}>
                  После перезапуска команда <Text code>/mcp</Text> покажет сервер{' '}
                  <Text code>lens</Text> и список инструментов <Text code>lens_*</Text>.
                </p>
              </>
            ),
          },
          {
            key: 'claude-desktop',
            label: 'Claude Desktop',
            children: (
              <>
                <p className={styles.hint}>
                  Настройки → Developer → Edit Config — откроется{' '}
                  <Text code>claude_desktop_config.json</Text>. Добавьте туда блок и полностью
                  перезапустите приложение (Quit, а не закрыть окно).
                </p>
                <CodeBlock code={jsonConfig} />
              </>
            ),
          },
          {
            key: 'codex',
            label: 'Codex CLI',
            children: (
              <>
                <p className={styles.hint}>
                  Блок добавляется в <Text code>~/.codex/config.toml</Text> (или в{' '}
                  <Text code>.codex/config.toml</Text> рядом с проектом).
                </p>
                <CodeBlock code={codexConfig} />
                <p className={styles.hint}>
                  Если не хочется держать токен в файле — положите его в переменную окружения и
                  укажите <Text code>bearer_token_env_var = "LENS_API_TOKEN"</Text> вместо{' '}
                  <Text code>http_headers</Text>.
                </p>
              </>
            ),
          },
          {
            key: 'cursor',
            label: 'Cursor',
            children: (
              <>
                <p className={styles.hint}>
                  Settings → MCP → Add new server, либо руками в{' '}
                  <Text code>~/.cursor/mcp.json</Text> (глобально) или{' '}
                  <Text code>.cursor/mcp.json</Text> в проекте.
                </p>
                <CodeBlock code={cursorConfig} />
              </>
            ),
          },
          {
            key: 'other',
            label: 'Другой клиент',
            children: (
              <>
                <p className={styles.hint}>
                  Подойдёт любой клиент с поддержкой MCP по HTTP. Где лежит конфиг — смотрите в его
                  документации, раздел «MCP» или «Интеграции»; содержимое блока одинаковое.
                </p>
                <CodeBlock code={jsonConfig} />
              </>
            ),
          },
        ]}
      />

      <p className={styles.hint}>
        Проверка: попросите ассистента «покажи список пространств в Lens» — должны приехать
        настоящие названия ваших пространств.
      </p>

      <h4 className={styles.subTitle}>Что станет доступно</h4>
      <ul className={styles.list}>
        <li>
          Чтение: пространства, типы записей, справочники, представления, поиск и просмотр записей.
        </li>
        <li>
          Запись: создание и правка записей, комментарии, создание пространств, типов, полей,
          представлений.
        </li>
        <li>Оценка трудоёмкости через Гермес — если она включена в пространстве.</li>
      </ul>
      <Alert
        type="info"
        showIcon
        className={styles.alert}
        message="Права проверяет сам Lens"
        description="Запись доступна с ролью «участник» или «администратор» в пространстве. Если прав не хватает, ассистент получит ошибку 403 — ничего не сломается."
      />

      <h4 className={styles.subTitle}>Если не работает</h4>
      <Table
        className={styles.table}
        dataSource={TROUBLES}
        rowKey="key"
        size="small"
        pagination={false}
        columns={[
          { title: 'Симптом', dataIndex: 'symptom', width: 280 },
          { title: 'Что делать', dataIndex: 'fix' },
        ]}
      />
    </div>
  );
}
